import React, { useCallback, useEffect, useState } from "react";
import { CloudOff, RefreshCw, UploadCloud } from "lucide-react";
import { getQueueCount } from "../../utils/offlineQueue";
import { v2_get_sync_status, v2_sync_push } from "../../actions/v2/sync";
import { useCurrentWorkingVessel } from "../../customHooks/useCurrentWorkingVessel";

const SyncStatusBadge = () => {
  const { vessel } = useCurrentWorkingVessel();
  const [localPending, setLocalPending] = useState(0);
  const [serverPending, setServerPending] = useState(0);
  const [syncState, setSyncState] = useState("idle");
  const [pushing, setPushing] = useState(false);
  const online = typeof navigator !== "undefined" ? navigator.onLine : true;

  const refresh = useCallback(async () => {
    const count = await getQueueCount();
    setLocalPending(count);

    if (vessel?.vessel_id && online) {
      try {
        const res = await v2_get_sync_status(vessel.vessel_id);
        if (res?.status) {
          const pendingOutbox = res.result?.pending_outbox ?? 0;
          const ledgerPending = res.result?.ledger_pending_sync ?? 0;
          setServerPending(pendingOutbox + ledgerPending);
          setSyncState(
            res.result?.failed
              ? "FAILED"
              : pendingOutbox
                ? "PENDING"
                : "IDLE"
          );
        }
      } catch {
        /* ignore when offline */
      }
    }
  }, [vessel?.vessel_id, online]);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener("elog-offline-queue-changed", onChange);
    window.addEventListener("elog-sync-flush", onChange);
    window.addEventListener("online", onChange);
    window.addEventListener("offline", onChange);
    const timer = setInterval(refresh, 30000);
    return () => {
      window.removeEventListener("elog-offline-queue-changed", onChange);
      window.removeEventListener("elog-sync-flush", onChange);
      window.removeEventListener("online", onChange);
      window.removeEventListener("offline", onChange);
      clearInterval(timer);
    };
  }, [refresh]);

  const totalPending = localPending + serverPending;

  if (totalPending === 0 && online) return null;

  if (!online) {
    return (
      <span
        className="sync-status-badge sync-status-badge--offline"
        title={`Offline — ${localPending} change(s) queued locally`}
      >
        <CloudOff size={14} /> Offline{localPending > 0 ? ` (${localPending})` : ""}
      </span>
    );
  }

  const pushNow = async () => {
    if (!vessel?.vessel_id || pushing) return;
    setPushing(true);
    try {
      await v2_sync_push(vessel.vessel_id);
    } finally {
      setPushing(false);
      refresh();
    }
  };

  return (
    <button
      type="button"
      className="sync-status-badge sync-status-badge--pending btn btn-link p-0"
      title={`Sync pending — local ${localPending}, server ${serverPending} (${syncState}). Click to push.`}
      onClick={pushNow}
      disabled={pushing}
    >
      {pushing ? <RefreshCw size={14} className="spin" /> : <UploadCloud size={14} />}
      Sync {totalPending}
    </button>
  );
};

export default SyncStatusBadge;
